import React, { useEffect, useMemo, useState } from "react";
import { format } from "lib/globalize-util";
import makeStyles from "@material-ui/core/styles/makeStyles";
import theme from "styles/theme";
import { ICellRendererParams } from "ag-grid-community";
import {
    fetchCustomizationProductById,
    fetchProductTemplate,
    resetCustomizationProduct,
    resetProductTemplate,
} from "actions/customization-actions";
import { connect, Dispatch } from "react-redux";
import { IStore } from "reducers";
import { IProduct } from "records/item/item";
import { ICustomizationTemplate } from "constants/product-customizations/customization";
import { ICartData } from "reducers/cart-grid-view";
import { EXCEEDED_CUSTOMIZATION_MODAL } from "constants/cart";
import { postToTopIframe } from "helpers/window-messages";
import CircularProgress from "material-ui/CircularProgress";
import { CUSTOMIZATIONS } from "../../constants/integration/customizations";

export interface IRendererDispatchProps {
    onFetchProductTemplate: (templateId: string) => void;
    onFetchProductById: (productID: string) => void;
    onResetCustomizationProduct: () => void;
    onResetCustomizationTemplate: () => void;
}

export interface IRendererStateProps {
    companyId: string;
    companyMerchantId: string;
    product: IProduct | null;
    modalIsOpened: boolean;
    workingOrderData: ICartData;
    template: ICustomizationTemplate;
    templateIsFetching: boolean;
    productIsFetching: boolean;
}

export const CustomizationRenderer = ({
    companyMerchantId,
    data,
    onFetchProductTemplate,
    onFetchProductById,
    product,
    modalIsOpened,
    workingOrderData,
    template,
    onResetCustomizationProduct,
    onResetCustomizationTemplate,
    templateIsFetching,
    productIsFetching,
}: ICellRendererParams & IRendererDispatchProps & IRendererStateProps) => {
    const classes = useStyles();
    const templateId = data?.item?.customization?.templateId;
    const [isEditClicked, setIsEditClicked] = useState<boolean>(false);

    const customizationsData = useMemo(() => {
        const selectedLineItem = workingOrderData?.order?.line_items.find(
            (lineItem) => lineItem._id === data?.item?.cartLineItemId
        );

        return selectedLineItem?.__customizations;
    }, [workingOrderData, data.item]);

    useEffect(() => {
        if (!template || !product || modalIsOpened || !templateId || !isEditClicked) {
            return;
        }

        setIsEditClicked(false);
        editLineItem(template, product);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, template, product, modalIsOpened, isEditClicked]);

    if (isEditClicked && (templateIsFetching || productIsFetching)) {
        return (
            <div className={classes.circularProgressContainer}>
                <CircularProgress mode="indeterminate" />
            </div>
        );
    }

    if (!templateId) {
        return null;
    }

    const handleEditCustomization = () => {
        const { productId }: { productId: string } = data || {};

        if (!templateId || !productId) {
            return;
        }

        onFetchProductTemplate(templateId);
        onFetchProductById(productId);
        setIsEditClicked(true);
    };

    const editLineItem = (customizationTemplate: ICustomizationTemplate, customizationProduct: IProduct) => {
        const { lineItemId } = data || {};
        const {
            name: productName,
            __product_customizations_threedium_model,
            __product_customizations_planar2d_model,
        } = product || {};

        postToTopIframe({
            action: EXCEEDED_CUSTOMIZATION_MODAL,
            payload: {
                productName,
                customizationProduct2dModel: __product_customizations_planar2d_model,
                customizationProduct3dModel: __product_customizations_threedium_model,
                companyMerchantId,
                customizations: customizationsData,
                customizationTemplate,
                lineItemId,
                customizationProduct,
            },
        });

        onResetCustomizationProduct();
        onResetCustomizationTemplate();
    };

    return (
        <div
            data-test={CUSTOMIZATIONS.EDIT_CUSTOMIZATION}
            className={classes.editButton}
            onClick={handleEditCustomization}
        >
            {format("Edit Customization")}
        </div>
    );
};

const mapDispatchToProps = (dispatch: Dispatch<IStore>): IRendererDispatchProps => ({
    onFetchProductTemplate: (templateId) => dispatch(fetchProductTemplate(templateId)),
    onFetchProductById: (productId) => dispatch(fetchCustomizationProductById(productId)),
    onResetCustomizationProduct: () => dispatch(resetCustomizationProduct()),
    onResetCustomizationTemplate: () => dispatch(resetProductTemplate()),
});

const mapStateToProps = (state: IStore): IRendererStateProps => {
    const { scope, customization, cartGridView } = state;
    return {
        companyId: scope?.company?._id,
        companyMerchantId: scope?.company?.__merchant_id,
        product: customization.selectedProduct.product,
        workingOrderData: cartGridView.data,
        modalIsOpened: !!customization?.editItem?.itemId,
        template: customization.current?.payload,
        templateIsFetching: customization.current?.isFetching,
        productIsFetching: customization.selectedProduct.isFetching,
    };
};

export default connect<IRendererStateProps, IRendererDispatchProps, ICellRendererParams>(
    mapStateToProps,
    mapDispatchToProps
)(CustomizationRenderer);

const useStyles = makeStyles({
    editButton: {
        textDecoration: "underline",
        cursor: "pointer",
        color: theme.palette.primary1Color,
    },
    circularProgressContainer: {
        width: "100%",
        height: "100%",
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        color: theme.palette.primary1Color,
    },
});
